// WebsitesListPage.js - Dedicated Browse Page for 100+ Ready-to-Use Business Websites
// Features comprehensive search, category filters, sorting, demo previews,
// and highlighted option to create a customized website for unlisted businesses.

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import SeoArticle from './SeoArticle';
import { WEBSITES_ARTICLE } from '../seo/seoContent';
import Navbar from './Navbar';
import Footer from './Footer';
import PreviewImage from './PreviewImage';
import CustomWebsiteModal from './CustomWebsiteModal';
import { PricingTeaser, WebsitesFaq } from './WebsitesSalesSections';
import { allTemplates, demoStoreUrl, templatesForVisitor } from './Hero';
import useDemoThemes from '../utils/demoThemes';
import { trackGetFreeClick } from '../utils/track';
import { useOwnezzyContact, whatsappLink } from '../legal/contact';
import './WebsitesListPage.css';

const PREVIEW_SIZES = '(max-width: 575px) calc(100vw - 58px), 380px';

const CATEGORY_FILTERS = [
  { id: 'all', label: 'All Websites (100+)', icon: '✨' },
  { id: 'fashion', label: 'Clothing & Fashion', icon: '👗', matches: ['clothing', 'beauty', 'kurti', 'blouses', 'pattu', 'linens', 'tshirts', 'sarees', 'dailywear', 'jewellery', 'bangles', 'artjewellery'] },
  { id: 'plants', label: 'Plants & Garden', icon: '🌱', matches: ['plants', 'gardentools'] },
  { id: 'gifts', label: 'Gifts & Favors', icon: '🎁', matches: ['gifts', 'favors', 'resin', 'crochet', 'acrylic', 'comics', 'wallclocks', 'candles'] },
  { id: 'tech', label: 'Phone Cases & Tech', icon: '📱', matches: ['tech', 'electronics'] },
  { id: 'footwear', label: 'Footwear & Juttis', icon: '👡', matches: ['footwear'] },
  { id: 'bags', label: 'Bags & Totes', icon: '👜', matches: ['bags'] },
  { id: 'crafts', label: 'Resin Crafts & Art', icon: '🧵', matches: ['crafts', 'resin', 'crochet'] },
  { id: 'decor', label: 'Home Decor & Urulis', icon: '🕯️', matches: ['decor', 'uruli', 'linens', 'acrylic', 'pooja', 'wallclocks', 'mirrors', 'cleaning', 'candles'] },
  { id: 'wellness', label: 'Beauty & Skincare', icon: '💄', matches: ['beauty', 'cosmetics', 'perfume', 'wellness', 'herbal', 'jewellery', 'bangles', 'artjewellery', 'coconutoil'] },
  { id: 'food', label: 'Grocery, Spices & Dry Fruits', icon: '🛒', matches: ['grocery', 'fresh', 'spices', 'dryfruits', 'organic', 'snacks', 'teacoffee', 'pickles', 'sweets', 'healthmix', 'bakery', 'tiffin', 'coconutoil'] },
  { id: 'toys', label: 'Toys & Kids', icon: '🧸', matches: ['toys', 'pattu'] },
  { id: 'health', label: 'Medical & Health', icon: '🩺', matches: ['medcare', 'organic'] },
  { id: 'textiles', label: 'Towels, Lungis & Linen', icon: '🧣', matches: ['bedbath', 'lungi', 'linens'] },
  { id: 'murti', label: 'Murtis & Pooja', icon: '🛕', matches: ['murti', 'uruli'] },
  { id: 'pets', label: 'Pet Store', icon: '🐾', matches: ['pets', 'aquarium'] },
];

export default function WebsitesListPage() {
  const contact = useOwnezzyContact();
  const templates = useDemoThemes(templatesForVisitor(allTemplates)); // the international stores at the top abroad

  // Parse initial query from URL search params (e.g. /websites?q=clothing)
  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('q') || '';
    } catch {
      return '';
    }
  });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular'); // 'popular' | 'name-asc' | 'name-desc'

  // Custom Website Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialCategory, setModalInitialCategory] = useState('');
  const [modalInitialTemplate, setModalInitialTemplate] = useState('');
  const [modalInitialPlan, setModalInitialPlan] = useState('');
  const [modalInitialLayout, setModalInitialLayout] = useState(''); // 'v2' when the card was a Studio demo

  // Category Pills Carousel Scroll State
  const pillsCarouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkPillsScroll = useCallback(() => {
    const el = pillsCarouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 6);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 6);
  }, []);

  useEffect(() => {
    checkPillsScroll();
    window.addEventListener('resize', checkPillsScroll);
    return () => window.removeEventListener('resize', checkPillsScroll);
  }, [checkPillsScroll]);

  const scrollPills = (direction) => {
    const el = pillsCarouselRef.current;
    if (!el) return;
    const scrollAmount = direction === 'left' ? -260 : 260;
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(checkPillsScroll, 320);
  };

  // Page SEO Meta Tags
  useEffect(() => {
    document.title = 'Create Website for Your Shop: 100+ Live Store Demos – Ownezzy';
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', 'Browse 100+ ready-to-launch online store websites for clothing, jewelry, footwear, phone cases, plants, and crafts. 7-day free trial, 0% commission & UPI.');
    setMeta('link[rel="canonical"]', 'href', 'https://ownezzy.com/websites');
    setMeta('meta[property="og:title"]', 'content', 'Create Website for Your Shop: 100+ Live Store Demos – Ownezzy');
    setMeta('meta[property="og:description"]', 'content', 'Browse 100+ ready-to-launch online store websites for clothing, jewelry, footwear, phone cases, plants, and crafts. 7-day free trial, 0% commission & UPI.');
    setMeta('meta[property="og:url"]', 'content', 'https://ownezzy.com/websites');
    window.scrollTo(0, 0);
  }, []);

  // Handle URL changes
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      if (q && q !== searchQuery) setSearchQuery(q);
    } catch {}
  }, []);

  const openCustomModal = (templateObjOrCategory = '', categoryName = '') => {
    if (typeof templateObjOrCategory === 'object' && templateObjOrCategory !== null) {
      const t = templateObjOrCategory;
      const resolvedTpl = t.template || t.id || '';
      setModalInitialTemplate(resolvedTpl);
      setModalInitialLayout(t.layout || '');
      setModalInitialCategory(t.label || t.business || '');
    } else if (typeof templateObjOrCategory === 'string') {
      setModalInitialTemplate(templateObjOrCategory || '');
      setModalInitialCategory(categoryName || searchQuery || '');
    } else {
      setModalInitialTemplate('');
      setModalInitialCategory(categoryName || searchQuery || '');
    }
    setModalInitialPlan('');
    if (typeof templateObjOrCategory !== 'object' || !templateObjOrCategory) setModalInitialLayout('');
    setIsModalOpen(true);
  };

  // Generic "start free" buttons in the marketing sections (optionally with a plan pre-selected)
  const startFreeTrial = (planKey = '') => {
    openCustomModal(searchQuery);
    setModalInitialPlan(planKey);
  };

  // Filter & Sort Logic
  const filteredTemplates = useMemo(() => {
    let list = [...templates];

    // Category filter
    if (selectedCategory !== 'all') {
      const catObj = CATEGORY_FILTERS.find((c) => c.id === selectedCategory);
      if (catObj && catObj.matches) {
        list = list.filter((t) => catObj.matches.includes(t.baseId || t.id));
      }
    }

    // Search query
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      list = list.filter((t) => {
        const haystack = `${t.name} ${t.label} ${t.note} ${t.business} ${t.pitch}`.toLowerCase();
        return haystack.includes(q);
      });
    }

    // Sort
    if (sortBy === 'name-asc') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      list.sort((a, b) => b.name.localeCompare(a.name));
    }

    return list;
  }, [templates, selectedCategory, searchQuery, sortBy]);

  return (
    <div className="websites-list-page-wrapper">
      <Navbar />

      <main className="websites-main-content">
        {/* Filter & Search Toolbar */}
        <section className="websites-toolbar-section">
          <div className="container">
            <div className="websites-toolbar-card">
              {/* Compact page title: keeps the store designs above the fold */}
              <div className="toolbar-title-row">
                <h1>100+ Ready-Made Online Store Websites</h1>
                <span className="toolbar-title-offer">🎉 Free for 7 days • No credit card</span>
              </div>

              {/* Search Bar */}
              <div className="toolbar-search-group">
                <span className="toolbar-search-icon" aria-hidden="true">
                  🔍
                </span>
                <input
                  type="text"
                  className="toolbar-search-input"
                  placeholder="Search websites by name, category or product (e.g. jewelry, footwear, candles)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="toolbar-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Category Pills Carousel */}
              <div className="toolbar-pills-carousel-wrapper">
                <button
                  type="button"
                  className="pills-carousel-nav-btn prev"
                  onClick={() => scrollPills('left')}
                  disabled={!canScrollLeft}
                  aria-label="Scroll categories left"
                >
                  ‹
                </button>
                <div
                  className="toolbar-category-pills"
                  ref={pillsCarouselRef}
                  onScroll={checkPillsScroll}
                  role="tablist"
                  aria-label="Website categories"
                >
                  {CATEGORY_FILTERS.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      role="tab"
                      aria-selected={selectedCategory === cat.id}
                      className={`cat-pill-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <span className="pill-icon">{cat.icon}</span>
                      <span className="pill-name">{cat.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="pills-carousel-nav-btn next"
                  onClick={() => scrollPills('right')}
                  disabled={!canScrollRight}
                  aria-label="Scroll categories right"
                >
                  ›
                </button>
              </div>

              {/* Active Results Summary & Sort Dropdown */}
              <div className="toolbar-results-bar">
                <span className="results-count">
                  Showing <strong>{filteredTemplates.length}</strong> {filteredTemplates.length === 1 ? 'website' : 'websites'}
                  {searchQuery && <span> matching &quot;{searchQuery}&quot;</span>}
                </span>

                <div className="toolbar-custom-cta">
                  <span className="toolbar-custom-text">Can&apos;t find your business?</span>
                  <button
                    type="button"
                    className="btn-toolbar-custom"
                    onClick={() => openCustomModal(searchQuery)}
                  >
                    ✨ Create Custom Website
                  </button>
                </div>

                <div className="toolbar-sort-group">
                  <label htmlFor="sort-select">Sort by:</label>
                  <select
                    id="sort-select"
                    className="toolbar-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="popular">Recommended / Most Popular</option>
                    <option value="name-asc">Name (A to Z)</option>
                    <option value="name-desc">Name (Z to A)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Website Cards Grid */}
        <section className="websites-grid-section">
          <div className="container">
            {filteredTemplates.length > 0 ? (
              <div className="websites-cards-grid">
                {filteredTemplates.map((t, idx) => {
                  const demoUrl = demoStoreUrl(t);
                  return (
                    <div
                      key={t.id}
                      className="website-store-card"
                      style={{
                        '--store-color': t.theme?.main || '#10b981',
                        '--store-soft': t.theme?.soft || '#ecfdf5',
                      }}
                    >
                      {/* Browser Frame Shot */}
                      <a
                        href={demoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="store-card-preview"
                        onClick={trackGetFreeClick}
                      >
                        <span className="store-card-frame">
                        <div className="store-browser-bar">
                          <span className="browser-dots">
                            <i /><i /><i />
                          </span>
                          <span className="browser-address">{t.demo}.ownezzy.com</span>
                        </div>
                        <PreviewImage
                          template={t}
                          sizes={PREVIEW_SIZES}
                          alt={`${t.name} - ${t.business || t.label} online store website with UPI payment`}
                          width="960"
                          height="984"
                          loading={idx < 4 ? 'eager' : 'lazy'}
                          fetchpriority={idx < 3 ? 'high' : 'auto'}
                        />
                        </span>
                        <span className="store-live-badge">
                          <i className="status-dot" /> Live Demo
                        </span>
                        <span className="store-category-tag">
                          <span className="cat-tag-icon">{t.icon}</span>
                          <span>{t.label}</span>
                        </span>
                      </a>

                      {/* Card Body */}
                      <div className="store-card-body">
                        {/* The kind of store first ("Clothing Store"), then the demo's brand name */}
                        <div className="store-card-header">
                          <h3 className="store-card-title"><span aria-hidden="true">{t.icon}</span> {t.label}</h3>
                          <span className="store-card-niche">Demo: {t.name}</span>
                        </div>
                        <p className="store-card-pitch">{t.pitch}</p>

                        {/* Action Buttons */}
                        <div className="store-card-actions">
                          <a
                            href={demoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-card-demo"
                            onClick={trackGetFreeClick}
                          >
                            <span className="demo-shine-sweep" aria-hidden="true" />
                            👀 View Demo
                          </a>
                          <button
                            type="button"
                            className="btn-card-launch"
                            onClick={() => openCustomModal(t)}
                          >
                            🚀 Start Free Trial
                          </button>
                        </div>
                        <p className="store-card-trial-note">7 days free • No credit card • Live in 60s</p>
                      </div>
                    </div>
                  );
                })}

                {/* Highlighted Banner Inside Grid */}
                <div className="custom-store-card-callout">
                  <span className="callout-sparkle">✨</span>
                  <h3>Don&apos;t See Your Exact Category?</h3>
                  <p>
                    We customize websites for ANY product or service — clothing, ceramics, jewelry, home baking, dry fruits, cosmetics, electronics, and more!
                  </p>
                  <button
                    type="button"
                    className="btn-callout-create"
                    onClick={() => openCustomModal(searchQuery)}
                  >
                    ✨ Build My Custom Website (60s)
                  </button>
                </div>
              </div>
            ) : (
              /* EMPTY STATE: NO MATCH FOUND */
              <div className="websites-empty-state">
                <span className="empty-icon">🔎</span>
                <h3>No pre-made website template found for &quot;{searchQuery}&quot;</h3>
                <p>
                  Don&apos;t worry! Ownezzy creates fully customized online stores with direct UPI payments, WhatsApp order notifications, and 0% commission for <strong>ANY</strong> product niche.
                </p>
                <div className="empty-action-group">
                  <button
                    type="button"
                    className="btn-empty-create-custom"
                    onClick={() => openCustomModal(searchQuery)}
                  >
                    ✨ Create Customized Website for &quot;{searchQuery}&quot;
                  </button>
                  <button
                    type="button"
                    className="btn-empty-reset"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                  >
                    Clear Search Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <PricingTeaser onStart={startFreeTrial} />

        <WebsitesFaq />

        <SeoArticle article={WEBSITES_ARTICLE} />
      </main>

      <Footer />

      {/* Floating WhatsApp Help Button */}
      <a
        href={whatsappLink(contact, 'Hi, I need help choosing or setting up an Ownezzy website.')}
        target="_blank"
        rel="noreferrer"
        className="whatsapp-float"
        aria-label="Need help? Chat with us on WhatsApp"
      >
        <i className="bi bi-whatsapp" />
        <span>Need help?</span>
      </a>

      {/* Mobile Sticky Free Trial Bar */}
      <div className="websites-mobile-trial-bar">
        <div className="mobile-trial-text">
          <strong>7-Day Free Trial</strong>
          <small>No credit card • 0% commission</small>
        </div>
        <div className="mobile-trial-actions">
          <a
            href={whatsappLink(contact, 'Hi, I need help choosing or setting up an Ownezzy website.')}
            target="_blank"
            rel="noreferrer"
            className="mobile-trial-wa"
            aria-label="Need help? Chat with us on WhatsApp"
          >
            <i className="bi bi-whatsapp" />
          </a>
          <button
            type="button"
            className="mobile-trial-btn"
            onClick={() => openCustomModal(searchQuery)}
          >
            🚀 Start Free
          </button>
        </div>
      </div>

      {/* Customized Website Modal */}
      <CustomWebsiteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialCategory={modalInitialCategory}
        initialTemplate={modalInitialTemplate}
        initialPlan={modalInitialPlan}
        initialLayout={modalInitialLayout}
      />
    </div>
  );
}
